/**
 * Demo-session-only chrome, split across two mount points:
 *
 *  - DemoTourChrome — the guided tour. Mounted in routes/__root.tsx, ABOVE
 *    every layout (`_app` AND the sibling /manage tree), because the default
 *    tour now walks the whole app: navigating from Plan (an `_app` route)
 *    into /manage swaps the entire layout, and a tour mounted inside `_app`
 *    would unmount mid-walkthrough. The tour needs nothing from the app
 *    shell — it portals to document.body and talks to the router directly.
 *
 *  - DemoFeedbackChrome — the floating feedback widget. Stays inside the
 *    `_app` shell (routes/_app.tsx): its placement logic reads the
 *    chat-panel + active-thread providers, which only exist there. On
 *    /manage pages the widget simply isn't rendered.
 *
 *  - DemoWelcomeChrome — the personalized welcome banner. Mounted at the top
 *    of the `_app` shell (inside SidebarInset, above the topbar/content), so
 *    it reads as part of the app chrome without disturbing the h-dvh layout.
 *
 *  - DemoFirstRunChrome — the first-run rail (the 0-90s scripted arc). Mounted
 *    in routes/__root.tsx beside the tour, for the same reason: the arc
 *    deep-links into the deliverable and the composer, so it must survive a
 *    layout swap. While it is pending it OWNS the first paint — the welcome
 *    banner (whose one line is the arc's opening state) and the tour's
 *    auto-start both stand down until it is done.
 *
 * All gate on `me.demo` (set by GET /me exclusively for demo users), so
 * normal deployments render nothing.
 */

import { useEffect, useState } from "react";

import { useAuth } from "@/auth";
import { postDemoEvent } from "@/lib/demoEvents";

import { DemoFeedbackWidget } from "./DemoFeedbackWidget";
import { DemoTour } from "./DemoTour";
import { DemoWelcomeBanner } from "./DemoWelcomeBanner";
import { DemoFirstRunRail, onDemoFirstRunReplay } from "./firstRun/DemoFirstRunRail";
import { parseFirstRunPayload, type FirstRunPayload } from "./firstRun/payload";
import { FIRST_RUN_DONE_EVENT, isFirstRunSeen } from "./firstRun/seen";
import { useGuideOpen } from "./guideOverlays";

/** Engagement beacons: one session_start per browser session, plus
 *  opened_hero the first time this session lands on the demo's hero page
 *  (the landing_path deliverable). Session-scoped guards — a hard refresh
 *  doesn't double-count, a fresh visit does. */
function useDemoBeacons(subdomain: string, userKey: string, landingPath: string) {
  useEffect(() => {
    if (!subdomain) return; // not a demo session (or /me not loaded yet)
    const key = `chai-demo-session:${subdomain}:${userKey}`;
    try {
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        postDemoEvent("session_start");
      }
    } catch {
      /* no storage -> skip the beacon rather than spam it */
    }
    const hero = (landingPath || "").trim();
    if (!hero) return;
    const heroKey = `chai-demo-hero:${subdomain}:${userKey}`;
    const fire = () => {
      try {
        if (sessionStorage.getItem(heroKey)) return;
        if (window.location.pathname.startsWith(hero)) {
          sessionStorage.setItem(heroKey, "1");
          postDemoEvent("opened_hero");
        }
      } catch {
        /* ignore */
      }
    };
    fire();
    const timer = window.setInterval(fire, 4000);
    return () => window.clearInterval(timer);
  }, [subdomain, userKey, landingPath]);
}

/** Root-level demo chrome: the guided tour (survives layout swaps). */
export function DemoTourChrome() {
  const { me } = useAuth();
  const demo = me?.demo;
  useDemoBeacons(demo?.subdomain ?? "", me?.user.id ?? "", demo?.landing_path ?? "");
  // No built-in fallback tour: a demo without authored steps mounts no tour.
  if (!demo || !(demo.tour?.steps?.length ?? 0)) return null;
  // Seen-flags are per USER, not just per demo — two personas sharing one
  // browser (live pitches, side-by-side testing) each get their own tour.
  return (
    <DemoTour
      demo={demo}
      userKey={me?.user.id ?? ""}
      firstName={(me?.user.name ?? "").trim().split(/\s+/)[0] ?? ""}
    />
  );
}

/** App-shell demo chrome: the feedback widget (needs the shell's providers). */
export function DemoFeedbackChrome() {
  const { me } = useAuth();
  const demo = me?.demo;
  if (!demo || demo.feedback_enabled === false) return null;
  return <DemoFeedbackWidget />;
}

/**
 * Is the first-run rail owed to this seat right now?
 *
 * Returns the validated payload while the arc is still pending and `null`
 * otherwise — no demo, no/invalid `demo_first_run`, or this seat already went
 * through it. Both the rail and the welcome banner read it, so the banner
 * stands down while the arc is running (its one line IS the arc's opening
 * state, and printing it twice reads as a bug).
 *
 * The seen-mark lives in localStorage, which React can't subscribe to, so the
 * rail fires FIRST_RUN_DONE_EVENT on dismissal and this hook re-reads.
 */
function useFirstRunPending(): FirstRunPayload | null {
  const { me } = useAuth();
  const demo = me?.demo;
  const userKey = me?.user.id ?? "";
  const [, bump] = useState(0);

  useEffect(() => {
    const onDone = () => bump((n) => n + 1);
    window.addEventListener(FIRST_RUN_DONE_EVENT, onDone);
    return () => window.removeEventListener(FIRST_RUN_DONE_EVENT, onDone);
  }, []);

  const payload = parseFirstRunPayload(demo?.first_run);
  if (!demo || !payload) return null;
  // Read at render, not in an effect, so a seat that has finished the arc
  // never flashes it again on a later navigation. `first_run_seen` is the
  // server's per-seat mark; it ends the arc on a second machine, where this
  // browser's own localStorage knows nothing.
  return isFirstRunSeen(demo.subdomain, userKey, demo.first_run_seen)
    ? null
    : payload;
}

/** Root-level demo chrome: the first-run rail — the scripted arc a prospect
 *  meets on first login. Mounted beside the tour (and above both layouts)
 *  because the arc navigates into the deliverable and the composer.
 *
 *  Renders nothing unless this demo carries a valid `demo_first_run` payload
 *  AND this seat hasn't already been through it — OR the prospect asked to see
 *  it again. Demos authored before the rail (every existing one) behave exactly
 *  as they did.
 *
 *  THE REPLAY. "Somebody who skimmed the first flow can never take that path
 *  again" was a real complaint about a real walkthrough, and the fix has to
 *  live HERE rather than in the rail: the rail is mounted on a seen-mark it
 *  cannot overrule from the inside. So the mount listens for the sidebar's
 *  replay request and mounts the same rail in a mode that writes no marks
 *  (./firstRun/seen.ts). A replay that arrives while the arc is genuinely
 *  pending changes nothing — the real thing is already on screen. */
export function DemoFirstRunChrome() {
  const { me } = useAuth();
  const demo = me?.demo;
  const pending = useFirstRunPending();
  const [replaying, setReplaying] = useState(false);

  useEffect(() => onDemoFirstRunReplay(() => setReplaying(true)), []);

  // The pending arc always wins: it is the one that counts.
  const replayPayload = replaying && !pending ? parseFirstRunPayload(demo?.first_run) : null;
  const payload = pending ?? replayPayload;
  if (!demo || !payload) return null;
  const mode = pending ? "auto" : "replay";
  return (
    <DemoFirstRunRail
      // Remounting on mode change resets the arc to its first state — which is
      // exactly what "replay" means, and what a retained `state` would break.
      key={mode}
      payload={payload}
      welcome={demo.welcome ?? ""}
      subdomain={demo.subdomain}
      userKey={me?.user.id ?? ""}
      clientName={demo.display_name}
      // The closing beat's exits: the conversion link and whether a guided
      // tour exists to offer (a demo with no tour must not render a tour
      // button that dismisses into nothing).
      bookingUrl={demo.booking_url ?? ""}
      hasTour={(demo.tour?.steps?.length ?? 0) > 0}
      mode={mode}
      onClose={() => setReplaying(false)}
    />
  );
}

/** App-shell demo chrome: the personalized welcome banner (top of the shell).
 *  Renders only while the demo carries a welcome message AND this browser
 *  hasn't dismissed it for this demo. */
export function DemoWelcomeChrome() {
  const { me } = useAuth();
  const demo = me?.demo;
  const message = demo?.welcome?.trim() ?? "";
  // Stand down while the first-run rail is pending — AND while one is on
  // screen at all: the rail's opening state shows this exact line, and two
  // copies of it read as a bug. Pending alone missed the REPLAY case (the
  // seen-mark stands, so nothing is "pending", but the replayed rail was
  // showing the identical paragraph under this banner — filmed live).
  //
  // And once a seat has FINISHED the arc, the banner retires for good on
  // rail demos: its one line was the arc's opening beat, already read —
  // re-pinning it across the top of every later session is repetition, not
  // orientation (audited live: "showing even though I've completed the
  // tour"). Demos with no rail keep the banner exactly as it was.
  const firstRunPending = useFirstRunPending() !== null;
  const railOpen = useGuideOpen("rail");
  const hasRail = parseFirstRunPayload(demo?.first_run) !== null;
  const railSeen =
    hasRail &&
    isFirstRunSeen(demo?.subdomain ?? "", me?.user.id ?? "", demo?.first_run_seen);
  if (!demo || !message || firstRunPending || railOpen || railSeen) return null;
  return (
    <DemoWelcomeBanner message={message} subdomain={demo.subdomain} userKey={me?.user.id ?? ""} />
  );
}
